import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import { useAuthStore } from "@/store/authStore";
import { PERMISSIONS_FALLBACK } from "@/lib/permissionsFallback";

/**
 * React Query hooks for the admin/tenant CRUD APIs — Slice 2 + 2b.
 *
 * Scoping: every tenant-scoped hook composes `actingAs.clientId` into its
 * `queryKey` (CROSS_PAGE_DECISIONS §13). The `apiClient` interceptor
 * forwards the active context to the backend via `X-Acting-As-Client`;
 * the queryKey suffix mirrors that context so React Query's cache stays
 * partitioned per impersonation. Without this suffix a SuperAdmin
 * flipping between tenants would see ghost data from the previous
 * tenant until the staleTime elapsed.
 *
 * Mutations do **not** need to include the suffix — `invalidateQueries`
 * matches by prefix, so a single invalidation nukes every impersonation
 * variant of the cached list.
 *
 * Hooks that intentionally bypass impersonation (e.g. `useClientsUnscoped`
 * powering the TenantSwitcher) call `apiClient` with `skipActingAs: true`
 * and **must not** include `actingAs.clientId` in their key.
 */

// -------------------------------------------------------------------------- //
// Types — mirror app/schemas/{client,customer,policy,branding}.py
// -------------------------------------------------------------------------- //

export interface ClientResponse {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  default_currency: string;
  status: string;
  created_at: string;
  updated_at: string;
  deactivated_at: string | null;
}

export interface ClientListRow extends ClientResponse {
  site_count: number;
  customer_count: number;
  user_count: number;
}

export interface BrandingBlock {
  primary_color: string;
  secondary_color: string;
  app_name: string;
  email_sender_name: string;
  logo_s3_key: string | null;
  favicon_s3_key: string | null;
  email_header_logo_s3_key: string | null;
  // P4 B28 — logo variants picked per (collapsed × theme) tuple
  // (ui.md §7.3).  Fallback ladder handled in the Sidebar consumer.
  mini_logo_s3_key: string | null;
  logo_dark_s3_key: string | null;
  mini_logo_dark_s3_key: string | null;
}

export interface BrandingUpdate {
  primary_color?: string | null;
  secondary_color?: string | null;
  app_name?: string | null;
  email_sender_name?: string | null;
  logo_s3_key?: string | null;
  favicon_s3_key?: string | null;
  email_header_logo_s3_key?: string | null;
  mini_logo_s3_key?: string | null;
  logo_dark_s3_key?: string | null;
  mini_logo_dark_s3_key?: string | null;
}

export interface ClientDetailResponse extends ClientListRow {
  branding: BrandingBlock;
}

export interface ClientCreate {
  // Slice 2c: slug + ticket prefix derived server-side from `name`.
  name: string;
  timezone?: string;
  default_currency?: string;
}

export interface ClientUpdate {
  name?: string;
  timezone?: string;
  default_currency?: string;
}

export interface ClientSettings {
  client_id: string;
  mfa_required: boolean;
  mfa_method: string | null;
  default_timezone: string | null;
  default_currency: string | null;
  prefix: string | null;
  updated_at: string;
}

export interface ClientSettingsUpdate {
  mfa_required?: boolean;
  mfa_method?: string | null;
  default_timezone?: string | null;
  default_currency?: string | null;
  prefix?: string | null;
}

export interface CustomerResponse {
  id: string;
  client_id: string;
  name: string;
  slug: string;
  timezone: string | null;
  default_currency: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  deactivated_at: string | null;
}

export interface CustomerListRow extends CustomerResponse {
  site_count: number;
  user_count: number;
}

export interface CustomerDetailResponse extends CustomerListRow {
  branding: BrandingBlock;
}

export interface CustomerCreate {
  // Slice 2c: slug derived server-side from `name`.
  name: string;
  timezone?: string | null;
  default_currency?: string | null;
  client_id?: string;
}

export interface CustomerUpdate {
  name?: string;
  timezone?: string | null;
  default_currency?: string | null;
}

export interface CustomerSettings {
  customer_id: string;
  mfa_required: boolean;
  mfa_method: string | null;
  default_timezone: string | null;
  default_currency: string | null;
  updated_at: string;
}

export interface CustomerSettingsUpdate {
  mfa_required?: boolean;
  mfa_method?: string | null;
  default_timezone?: string | null;
  default_currency?: string | null;
}

/**
 * MV2-243: ``PolicySiteScope`` is retained as a UI-only enum for the
 * onboarding scope-mode picker ("All client sites" / "Customer-scoped" /
 * "Specific sites") via ``UserScopeMode``. It is NOT a property of a
 * policy any more — policies are pure roles.
 */

export interface PolicyResponse {
  id: string;
  client_id: string | null;
  name: string;
  description: string | null;
  is_global: boolean;
  features: string[];
  report_types: string[];
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface PolicyCreate {
  name: string;
  description?: string | null;
  features?: string[];
  report_types?: string[];
  client_id?: string;
}

export interface PolicyUpdate {
  name?: string;
  description?: string | null;
  features?: string[];
  report_types?: string[];
}

export interface FeatureCatalog {
  features: string[];
  super_admin_only: string[];
  report_types: string[];
}

// MV2-199: rich metadata served by GET /permissions/feature-catalog,
// consumed by the redesigned PolicyEditor for grouped layout + live
// persona-class preview. Mirror of `app.services.feature_catalog`'s
// TypedDicts.
export interface PermissionsFeatureMeta {
  key: string;
  label: string;
  description: string;
  group: string;
  also_gates: string[];
}

export interface PermissionsGroupMeta {
  key: string;
  label: string;
  icon: string;
  order: number;
  description: string;
}

export type PermissionsPersonaClass =
  | "SuperAdmin"
  | "ClientAdmin"
  | "OM"
  | "SiteOwner";

export interface PermissionsCatalog {
  features: PermissionsFeatureMeta[];
  groups: PermissionsGroupMeta[];
  persona_rules: { om_signature: string[] };
  persona_classes: PermissionsPersonaClass[];
}

// -------------------------------------------------------------------------- //
// Clients
// -------------------------------------------------------------------------- //

export function useClients() {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<ClientListRow[]>({
    queryKey: ["admin", "clients", actingAsClientId],
    queryFn: async () =>
      (await apiClient.get<ClientListRow[]>("/admin/clients")).data,
    staleTime: 30_000,
  });
}

/**
 * R3 fix — TenantSwitcher-only client list that bypasses the
 * X-Acting-As-Client header so the dropdown always shows every client a
 * SuperAdmin can impersonate, regardless of which one is currently
 * active. The default `useClients` pairs with the backend's scoped
 * `list_clients` which narrows the result to the acting-as client — good
 * for admin-list pages, terrible for the switcher because after picking
 * client A the list collapses to just A and the operator can't get back.
 */
export function useClientsUnscoped() {
  return useQuery<ClientListRow[]>({
    queryKey: ["admin", "clients", "unscoped"],
    queryFn: async () =>
      (
        await apiClient.get<ClientListRow[]>("/admin/clients", {
          // Interceptor checks this flag and skips the X-Acting-As-Client
          // header — see src/api/client.ts.
          skipActingAs: true,
        })
      ).data,
    staleTime: 30_000,
  });
}

export function useClient(clientId: string | undefined) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<ClientDetailResponse>({
    queryKey: ["admin", "client", clientId, actingAsClientId],
    queryFn: async () =>
      (await apiClient.get<ClientDetailResponse>(`/admin/clients/${clientId}`))
        .data,
    enabled: Boolean(clientId),
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation<ClientDetailResponse, Error, ClientCreate>({
    mutationFn: async (body) =>
      (await apiClient.post<ClientDetailResponse>("/admin/clients", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "clients"] }),
  });
}

export function useUpdateClient(clientId: string) {
  const qc = useQueryClient();
  return useMutation<ClientDetailResponse, Error, ClientUpdate>({
    mutationFn: async (body) =>
      (
        await apiClient.patch<ClientDetailResponse>(
          `/admin/clients/${clientId}`,
          body,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "clients"] });
      qc.invalidateQueries({ queryKey: ["admin", "client", clientId] });
    },
  });
}

export function useDeactivateClient() {
  const qc = useQueryClient();
  return useMutation<ClientResponse, Error, string>({
    mutationFn: async (clientId) =>
      (
        await apiClient.post<ClientResponse>(
          `/admin/clients/${clientId}/deactivate`,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "clients"] }),
  });
}

export function useReactivateClient() {
  const qc = useQueryClient();
  return useMutation<ClientResponse, Error, string>({
    mutationFn: async (clientId) =>
      (
        await apiClient.post<ClientResponse>(
          `/admin/clients/${clientId}/reactivate`,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "clients"] }),
  });
}

export function useClientBranding(clientId: string | undefined) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<BrandingBlock>({
    queryKey: ["admin", "client-branding", clientId, actingAsClientId],
    queryFn: async () =>
      (
        await apiClient.get<BrandingBlock>(
          `/admin/clients/${clientId}/branding`,
        )
      ).data,
    enabled: Boolean(clientId),
  });
}

export function useUpdateClientBranding(clientId: string) {
  const qc = useQueryClient();
  return useMutation<BrandingBlock, Error, BrandingUpdate>({
    mutationFn: async (body) =>
      (
        await apiClient.patch<BrandingBlock>(
          `/admin/clients/${clientId}/branding`,
          body,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["admin", "client-branding", clientId],
      });
      qc.invalidateQueries({ queryKey: ["admin", "client", clientId] });
      // Any /me consumer's branding may change too if SuperAdmin is acting-as.
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useClientSettings(clientId: string | undefined) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<ClientSettings>({
    queryKey: ["admin", "client-settings", clientId, actingAsClientId],
    queryFn: async () =>
      (
        await apiClient.get<ClientSettings>(
          `/admin/clients/${clientId}/settings`,
        )
      ).data,
    enabled: Boolean(clientId),
  });
}

export function useUpdateClientSettings(clientId: string) {
  const qc = useQueryClient();
  return useMutation<ClientSettings, Error, ClientSettingsUpdate>({
    mutationFn: async (body) =>
      (
        await apiClient.patch<ClientSettings>(
          `/admin/clients/${clientId}/settings`,
          body,
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ["admin", "client-settings", clientId],
      }),
  });
}

// -------------------------------------------------------------------------- //
// Customers
// -------------------------------------------------------------------------- //

export function useCustomers(clientId?: string | null) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<CustomerListRow[]>({
    // clientId (per-list filter) AND actingAsClientId (impersonation
    // context) are both in the key so flipping either re-fetches.
    queryKey: ["customers", clientId ?? null, actingAsClientId],
    queryFn: async () =>
      (
        await apiClient.get<CustomerListRow[]>("/customers", {
          params: clientId ? { client_id: clientId } : undefined,
        })
      ).data,
    staleTime: 30_000,
  });
}

export function useCustomer(customerId: string | undefined) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<CustomerDetailResponse>({
    queryKey: ["customer", customerId, actingAsClientId],
    queryFn: async () =>
      (await apiClient.get<CustomerDetailResponse>(`/customers/${customerId}`))
        .data,
    enabled: Boolean(customerId),
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation<CustomerDetailResponse, Error, CustomerCreate>({
    mutationFn: async (body) =>
      (await apiClient.post<CustomerDetailResponse>("/customers", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });
}

export function useUpdateCustomer(customerId: string) {
  const qc = useQueryClient();
  return useMutation<CustomerDetailResponse, Error, CustomerUpdate>({
    mutationFn: async (body) =>
      (
        await apiClient.patch<CustomerDetailResponse>(
          `/customers/${customerId}`,
          body,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["customer", customerId] });
    },
  });
}

export function useDeactivateCustomer() {
  const qc = useQueryClient();
  return useMutation<CustomerResponse, Error, string>({
    mutationFn: async (customerId) =>
      (
        await apiClient.post<CustomerResponse>(
          `/customers/${customerId}/deactivate`,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });
}

export function useCustomerSettings(customerId: string | undefined) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<CustomerSettings>({
    queryKey: ["customer-settings", customerId, actingAsClientId],
    queryFn: async () =>
      (
        await apiClient.get<CustomerSettings>(
          `/customers/${customerId}/settings`,
        )
      ).data,
    enabled: Boolean(customerId),
  });
}

export function useUpdateCustomerSettings(customerId: string) {
  const qc = useQueryClient();
  return useMutation<CustomerSettings, Error, CustomerSettingsUpdate>({
    mutationFn: async (body) =>
      (
        await apiClient.patch<CustomerSettings>(
          `/customers/${customerId}/settings`,
          body,
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["customer-settings", customerId] }),
  });
}

export function useUpdateCustomerBranding(customerId: string) {
  const qc = useQueryClient();
  return useMutation<BrandingBlock, Error, BrandingUpdate>({
    mutationFn: async (body) =>
      (
        await apiClient.patch<BrandingBlock>(
          `/branding/customer/${customerId}`,
          body,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer", customerId] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// -------------------------------------------------------------------------- //
// Policies
// -------------------------------------------------------------------------- //

export function usePolicies(clientId?: string | null) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<PolicyResponse[]>({
    queryKey: ["policies", clientId ?? null, actingAsClientId],
    queryFn: async () =>
      (
        await apiClient.get<PolicyResponse[]>("/policies", {
          params: clientId ? { client_id: clientId } : undefined,
        })
      ).data,
    staleTime: 60_000,
  });
}

export function usePolicy(policyId: string | undefined) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<PolicyResponse>({
    queryKey: ["policy", policyId, actingAsClientId],
    queryFn: async () =>
      (await apiClient.get<PolicyResponse>(`/policies/${policyId}`)).data,
    enabled: Boolean(policyId),
  });
}

export function useFeatureCatalog() {
  return useQuery<FeatureCatalog>({
    queryKey: ["policies", "features"],
    queryFn: async () =>
      (await apiClient.get<FeatureCatalog>("/policies/features")).data,
    staleTime: Infinity, // static vocabulary
  });
}

// MV2-199: catalog metadata for the redesigned PolicyEditor (grouped
// layout + persona-class preview). Static across every signed-in user
// — no `actingAs` key needed and the response is cached forever per
// app load. `placeholderData` keeps the editor functional during the
// brief deploy window where the bundle ships before backend PR #221.
export function usePermissionsCatalog() {
  return useQuery<PermissionsCatalog>({
    queryKey: ["permissions", "feature-catalog"],
    queryFn: async () =>
      (await apiClient.get<PermissionsCatalog>("/permissions/feature-catalog"))
        .data,
    staleTime: Infinity,
    placeholderData: PERMISSIONS_FALLBACK,
    retry: 0,
  });
}

export function useCreatePolicy() {
  const qc = useQueryClient();
  return useMutation<PolicyResponse, Error, PolicyCreate>({
    mutationFn: async (body) =>
      (await apiClient.post<PolicyResponse>("/policies", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["policies"] }),
  });
}

export function useUpdatePolicy(policyId: string) {
  const qc = useQueryClient();
  return useMutation<PolicyResponse, Error, PolicyUpdate>({
    mutationFn: async (body) =>
      (await apiClient.patch<PolicyResponse>(`/policies/${policyId}`, body))
        .data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["policies"] });
      qc.invalidateQueries({ queryKey: ["policy", policyId] });
    },
  });
}

export function useDeletePolicy() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (policyId) => {
      await apiClient.delete(`/policies/${policyId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["policies"] }),
  });
}
